import { z } from "zod";
import { ADDRESS_FIELDS, contactInputSchema, EMAIL_FIELDS, PHONE_FIELDS, TEXT_FIELDS } from "./contact.js";
import { CONNECTION_STATUSES, LIST_COLORS, RECIPIENT_STATUSES, SYNC_TRIGGERS } from "./enums.js";

/*
 * Public REST API and webhooks (issue #32). The zod schemas here validate requests on the server,
 * and buildOpenApi() turns them into the OpenAPI document (served at /api/v1/openapi.json) and the
 * documentation page on the landing site. One source, so the documentation cannot drift.
 */

export const API_KEY_SCOPES = ["read", "write"] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];
export const API_KEY_EXPIRY_DAYS = [30, 90, 365] as const;
export const API_RATE_LIMIT = { requests: 600, windowSeconds: 60 } as const;

export const WEBHOOK_EVENTS = [
  "contact.created",
  "contact.updated",
  "contact.deleted",
  "list.created",
  "list.updated",
  "list.deleted",
  "list.contacts_changed",
  "sync.completed",
  "delivery.failed",
  "recipient.unreachable",
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
/** Sent by "Send test event"; endpoints always receive it. */
export const WEBHOOK_TEST_EVENT = "webhook.test";

/* ---------- Managing keys and endpoints (Settings > API, administrators) ---------- */

export const apiKeyCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  scope: z.enum(API_KEY_SCOPES),
  /** null: all lists. Otherwise the key works like a list editor of these lists. */
  listIds: z.array(z.string().uuid()).min(1).max(100).nullable(),
  /** null: no expiry. */
  expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(365)]).nullable(),
});

export type ApiKeyInfo = {
  id: string;
  name: string;
  /** First characters of the key, to recognize it. */
  prefix: string;
  scope: ApiKeyScope;
  listIds: string[] | null;
  createdAt: string;
  createdBy: string | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
};
/** The key itself is only in this response. */
export type ApiKeyCreated = ApiKeyInfo & { key: string };

export const webhookEndpointCreateSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2000)
    .url()
    .refine((u) => {
      const url = new URL(u);
      return url.protocol === "https:" && !url.username && !url.password;
    }, "https_required"),
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1),
});

export type WebhookEndpointInfo = {
  id: string;
  url: string;
  events: WebhookEvent[];
  createdAt: string;
  lastDeliveryAt: string | null;
  /** HTTP status of the last attempt; 0 when the server could not be reached. */
  lastStatus: number | null;
  lastError: string | null;
  /** Deliveries still waiting for a retry. */
  pending: number;
};
/** The signing secret is only in this response. */
export type WebhookEndpointCreated = WebhookEndpointInfo & { secret: string };

/* ---------- API v1: requests ---------- */

const uuid = z.string().uuid();

export const publicContactQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  listId: uuid.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/** Contact fields plus the lists a new contact goes into (required for keys limited to lists). */
export const publicContactCreateSchema = z.object({ listIds: z.array(uuid).max(100).default([]) });

/** Manual lists only: Microsoft 365 directory and file lists are set up in the app. */
export const publicListCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullish(),
  color: z.enum(LIST_COLORS).default("iris"),
});
export const publicListUpdateSchema = publicListCreateSchema.partial();

/* ---------- API v1: responses ---------- */

const nullableText = z.string().nullable();
const addressOut = z
  .object({
    street: z.string(),
    postalCode: z.string(),
    city: z.string(),
    state: z.string(),
    country: z.string(),
  })
  .nullable();

export const publicContactSchema = z.object({
  id: uuid,
  displayName: z.string(),
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f, nullableText])),
  ...Object.fromEntries(EMAIL_FIELDS.map((f) => [f, nullableText])),
  ...Object.fromEntries(PHONE_FIELDS.map((f) => [f, nullableText])),
  ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, addressOut])),
  listIds: z.array(uuid),
  source: z.enum(["manual", "directory", "file"]),
  readOnly: z.boolean(),
  hasPhoto: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PublicContact = z.infer<typeof publicContactSchema>;

export const publicListSchema = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  color: z.enum(LIST_COLORS),
  kind: z.enum(["manual", "directory", "file"]),
  contactCount: z.number().int(),
  readOnly: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PublicList = z.infer<typeof publicListSchema>;

export const publicSyncStatusSchema = z.object({
  connection: z.enum(CONNECTION_STATUSES),
  running: z.boolean(),
  lastSuccessAt: z.string().nullable(),
  recipients: z.object(Object.fromEntries(RECIPIENT_STATUSES.map((s) => [s, z.number().int()]))),
  lastRun: z
    .object({
      id: uuid,
      trigger: z.enum(SYNC_TRIGGERS),
      status: z.enum(["running", "done", "failed", "cancelled"]),
      startedAt: z.string(),
      finishedAt: z.string().nullable(),
      created: z.number().int(),
      updated: z.number().int(),
      deleted: z.number().int(),
      errors: z.number().int(),
    })
    .nullable(),
});
export type PublicSyncStatus = z.infer<typeof publicSyncStatusSchema>;

const errorSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

const pageOf = (item: z.ZodType) =>
  z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
  });

/* ---------- OpenAPI ---------- */

export type DocLocale = "en" | "de";
type Text = { en: string; de: string };
type JsonSchema = Record<string, unknown> & { properties?: Record<string, JsonSchema> };

/** Descriptions of fields whose meaning or format is not obvious from the name. */
const FIELD_DOCS: Record<string, Text> = {
  displayName: {
    en: "Built from given, middle and surname, or the company. Read-only.",
    de: "Aus Vor-, Zweit- und Nachname oder der Firma gebildet. Nur lesen.",
  },
  surname: {
    en: "givenName, surname or companyName is required.",
    de: "givenName, surname oder companyName ist Pflicht.",
  },
  officeLocation: { en: "Site or office.", de: "Standort oder Büro." },
  imAddress: { en: "Teams or SIP address.", de: "Teams- oder SIP-Adresse." },
  website: { en: "https:// is added when missing.", de: "https:// wird ergänzt, wenn es fehlt." },
  ...Object.fromEntries(
    PHONE_FIELDS.map((f) => [
      f,
      {
        en: "Any common format, Swiss numbers without country code too. Returned in E.164 (+41...).",
        de: "Jedes übliche Format, Schweizer Nummern auch ohne Ländervorwahl. Zurück kommt E.164 (+41...).",
      },
    ]),
  ),
  ...Object.fromEntries(
    ADDRESS_FIELDS.map((f) => [
      f,
      {
        en: "Object with street, postalCode, city, state, country.",
        de: "Objekt mit street, postalCode, city, state, country.",
      },
    ]),
  ),
  listIds: {
    en: "Ids of the lists the contact is in (only lists the key may see).",
    de: "Ids der Listen, in denen der Kontakt ist (nur Listen, die der Schlüssel sieht).",
  },
  source: {
    en: "manual: maintained in Kontaktfix. directory: employee from Microsoft 365. file: from a file list.",
    de: "manual: in Kontaktfix gepflegt. directory: Mitarbeitende aus Microsoft 365. file: aus einer Dateiliste.",
  },
  readOnly: {
    en: "Directory and file contacts cannot be changed through the API.",
    de: "Kontakte aus Verzeichnis- und Dateilisten können über die API nicht geändert werden.",
  },
  kind: {
    en: "manual, directory (Microsoft 365) or file (CSV/XLSX). Only manual lists take contacts through the API.",
    de: "manual, directory (Microsoft 365) oder file (CSV/XLSX). Nur manuelle Listen nehmen über die API Kontakte auf.",
  },
  contactCount: { en: "Contacts in the list.", de: "Kontakte in der Liste." },
  running: { en: "A delivery is in progress.", de: "Eine Verteilung läuft gerade." },
  lastSuccessAt: {
    en: "Last time a mailbox was brought up to date.",
    de: "Letzter Zeitpunkt, an dem ein Postfach aktualisiert wurde.",
  },
  recipients: { en: "People by delivery status.", de: "Personen nach Verteilstatus." },
};

const PARAM_DOCS: Record<string, Text> = {
  q: {
    en: "Search in name, company, job title, email, notes and phone numbers.",
    de: "Suche in Name, Firma, Funktion, E-Mail, Notizen und Telefonnummern.",
  },
  listId: {
    en: "Only contacts in this list. Without it, employees from Microsoft 365 are left out.",
    de: "Nur Kontakte dieser Liste. Ohne diesen Parameter fehlen die Mitarbeitenden aus Microsoft 365.",
  },
  page: { en: "Page, from 1.", de: "Seite, ab 1." },
  pageSize: { en: "Items per page, 1 to 200.", de: "Einträge pro Seite, 1 bis 200." },
};

function jsonSchema(schema: z.ZodType, locale: DocLocale, io: "input" | "output" = "output"): JsonSchema {
  // Without the safe-integer bounds zod adds to every integer: noise in the documentation.
  const out = JSON.parse(JSON.stringify(z.toJSONSchema(schema, { io, unrepresentable: "any" })), (k, v) =>
    (k === "minimum" || k === "maximum") && Math.abs(v) === Number.MAX_SAFE_INTEGER ? undefined : v,
  ) as JsonSchema;
  delete out.$schema;
  for (const [name, prop] of Object.entries(out.properties ?? {})) {
    const doc = FIELD_DOCS[name];
    if (doc) prop.description = doc[locale];
  }
  return out;
}

type Operation = {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  tag: "Contacts" | "Lists" | "Sync";
  id: string;
  summary: Text;
  description: Text;
  scope: ApiKeyScope;
  /** The key must not be limited to lists. */
  allLists?: boolean;
  query?: z.ZodObject;
  body?: JsonSchema;
  response: JsonSchema | null;
  example?: unknown;
  requestExample?: unknown;
};

const EXAMPLE_CONTACT = {
  id: "4b1f6a3e-2c1d-4a8e-9f5b-0c7d2e9a1b34",
  displayName: "Anna Meier",
  title: null,
  givenName: "Anna",
  middleName: null,
  surname: "Meier",
  nickName: null,
  companyName: "Muster Haustechnik AG",
  department: "Service",
  jobTitle: "Disponentin",
  profession: null,
  officeLocation: "Zürich",
  manager: null,
  assistantName: null,
  imAddress: null,
  website: "https://muster-haustechnik.ch",
  notes: "Pikett: 24 h",
  email1: "anna.meier@muster-haustechnik.ch",
  email2: null,
  email3: null,
  businessPhone1: "+41441234567",
  businessPhone2: null,
  mobilePhone: "+41791234567",
  homePhone1: null,
  homePhone2: null,
  businessAddress: {
    street: "Bahnhofstrasse 1",
    postalCode: "8001",
    city: "Zürich",
    state: "ZH",
    country: "Schweiz",
  },
  homeAddress: null,
  otherAddress: null,
  listIds: ["9e2c4d1a-7b3f-4e8a-a1c5-3d6f8b0e2a71"],
  source: "manual",
  readOnly: false,
  hasPhoto: false,
  createdAt: "2026-10-20T08:15:00.000Z",
  updatedAt: "2026-10-24T13:42:10.000Z",
};

const EXAMPLE_LIST = {
  id: "9e2c4d1a-7b3f-4e8a-a1c5-3d6f8b0e2a71",
  name: "Lieferanten",
  description: "Alle Lieferanten mit Pikettnummern",
  color: "iris",
  kind: "manual",
  contactCount: 128,
  readOnly: false,
  createdAt: "2026-09-02T09:00:00.000Z",
  updatedAt: "2026-10-24T13:42:10.000Z",
};

function operations(locale: DocLocale): Operation[] {
  const contact = jsonSchema(publicContactSchema, locale);
  const list = jsonSchema(publicListSchema, locale);
  const contactInput = jsonSchema(contactInputSchema, locale, "input");
  const contactCreate: JsonSchema = {
    ...contactInput,
    properties: {
      ...contactInput.properties,
      listIds: {
        type: "array",
        items: { type: "string", format: "uuid" },
        description:
          locale === "de"
            ? "Listen, in die der Kontakt kommt. Pflicht für Schlüssel, die auf Listen beschränkt sind."
            : "Lists the contact goes into. Required for keys limited to lists.",
      },
    },
  };
  const contactPatch: JsonSchema = { ...contactInput, required: undefined };
  const listCreate = jsonSchema(publicListCreateSchema, locale, "input");
  const listUpdate = jsonSchema(publicListUpdateSchema, locale, "input");
  return [
    {
      method: "get",
      path: "/contacts",
      tag: "Contacts",
      id: "listContacts",
      summary: { en: "List contacts", de: "Kontakte auflisten" },
      description: {
        en: "Contacts sorted by name, page by page. Keys limited to lists only see contacts in those lists.",
        de: "Kontakte nach Name sortiert, seitenweise. Schlüssel mit Listenbeschränkung sehen nur Kontakte dieser Listen.",
      },
      scope: "read",
      query: publicContactQuerySchema,
      response: jsonSchema(pageOf(publicContactSchema), locale),
      example: { items: [EXAMPLE_CONTACT], total: 1, page: 1, pageSize: 50 },
    },
    {
      method: "get",
      path: "/contacts/{id}",
      tag: "Contacts",
      id: "getContact",
      summary: { en: "Get a contact", de: "Kontakt abrufen" },
      description: { en: "One contact with all fields.", de: "Ein Kontakt mit allen Feldern." },
      scope: "read",
      response: contact,
      example: EXAMPLE_CONTACT,
    },
    {
      method: "post",
      path: "/contacts",
      tag: "Contacts",
      id: "createContact",
      requestExample: {
        givenName: "Anna",
        surname: "Meier",
        companyName: "Muster Haustechnik AG",
        mobilePhone: "079 123 45 67",
        email1: "anna.meier@muster-haustechnik.ch",
        listIds: ["9e2c4d1a-7b3f-4e8a-a1c5-3d6f8b0e2a71"],
      },
      summary: { en: "Create a contact", de: "Kontakt erstellen" },
      description: {
        en: "Creates the contact and puts it into the given lists. Delivery to the mailboxes starts right away.",
        de: "Erstellt den Kontakt und legt ihn in die angegebenen Listen. Die Verteilung in die Postfächer startet sofort.",
      },
      scope: "write",
      body: contactCreate,
      response: contact,
      example: EXAMPLE_CONTACT,
    },
    {
      method: "patch",
      path: "/contacts/{id}",
      tag: "Contacts",
      id: "updateContact",
      requestExample: { jobTitle: "Leiterin Disposition", businessPhone2: null },
      summary: { en: "Update a contact", de: "Kontakt ändern" },
      description: {
        en: "Changes only the fields you send. Send null or an empty string to clear a field. List membership is changed with the list endpoints.",
        de: "Ändert nur die mitgesendeten Felder. null oder ein leerer Text leert ein Feld. Die Listenzugehörigkeit ändern Sie über die Listen-Endpunkte.",
      },
      scope: "write",
      body: contactPatch,
      response: contact,
      example: EXAMPLE_CONTACT,
    },
    {
      method: "delete",
      path: "/contacts/{id}",
      tag: "Contacts",
      id: "deleteContact",
      summary: { en: "Delete a contact", de: "Kontakt löschen" },
      description: {
        en: "Deletes the contact and removes it from all mailboxes.",
        de: "Löscht den Kontakt und entfernt ihn aus allen Postfächern.",
      },
      scope: "write",
      allLists: true,
      response: null,
    },
    {
      method: "get",
      path: "/lists",
      tag: "Lists",
      id: "listLists",
      summary: { en: "List lists", de: "Listen auflisten" },
      description: {
        en: "All lists the key may see, sorted by name.",
        de: "Alle Listen, die der Schlüssel sieht, nach Name sortiert.",
      },
      scope: "read",
      response: { type: "array", items: list },
      example: [EXAMPLE_LIST],
    },
    {
      method: "get",
      path: "/lists/{id}",
      tag: "Lists",
      id: "getList",
      summary: { en: "Get a list", de: "Liste abrufen" },
      description: { en: "One list.", de: "Eine Liste." },
      scope: "read",
      response: list,
      example: EXAMPLE_LIST,
    },
    {
      method: "post",
      path: "/lists",
      tag: "Lists",
      id: "createList",
      requestExample: {
        name: "Lieferanten",
        description: "Alle Lieferanten mit Pikettnummern",
        color: "iris",
      },
      summary: { en: "Create a list", de: "Liste erstellen" },
      description: {
        en: "Creates a manual list. Who receives it is set in the app (Lists > Assignments).",
        de: "Erstellt eine manuelle Liste. Wer sie erhält, legen Sie in der App fest (Listen > Zuweisungen).",
      },
      scope: "write",
      allLists: true,
      body: listCreate,
      response: list,
      example: EXAMPLE_LIST,
    },
    {
      method: "patch",
      path: "/lists/{id}",
      tag: "Lists",
      id: "updateList",
      requestExample: { name: "Lieferanten Schweiz" },
      summary: { en: "Update a list", de: "Liste ändern" },
      description: {
        en: "Changes name, description or color. The name is the Outlook category, so a rename updates the delivered contacts.",
        de: "Ändert Name, Beschreibung oder Farbe. Der Name ist die Outlook-Kategorie, eine Umbenennung aktualisiert deshalb die verteilten Kontakte.",
      },
      scope: "write",
      allLists: true,
      body: listUpdate,
      response: list,
      example: EXAMPLE_LIST,
    },
    {
      method: "delete",
      path: "/lists/{id}",
      tag: "Lists",
      id: "deleteList",
      summary: { en: "Delete a list", de: "Liste löschen" },
      description: {
        en: "Deletes the list. Its contacts stay, but leave the mailboxes that received them only through this list.",
        de: "Löscht die Liste. Die Kontakte bleiben, verschwinden aber aus den Postfächern, die sie nur über diese Liste erhielten.",
      },
      scope: "write",
      allLists: true,
      response: null,
    },
    {
      method: "put",
      path: "/lists/{id}/contacts/{contactId}",
      tag: "Lists",
      id: "addContactToList",
      summary: { en: "Add a contact to a list", de: "Kontakt zu Liste hinzufügen" },
      description: {
        en: "Idempotent: adding a contact that is already in the list changes nothing.",
        de: "Idempotent: Ein Kontakt, der schon in der Liste ist, bleibt unverändert.",
      },
      scope: "write",
      response: null,
    },
    {
      method: "delete",
      path: "/lists/{id}/contacts/{contactId}",
      tag: "Lists",
      id: "removeContactFromList",
      summary: { en: "Remove a contact from a list", de: "Kontakt aus Liste entfernen" },
      description: {
        en: "The contact itself stays. Idempotent.",
        de: "Der Kontakt selbst bleibt bestehen. Idempotent.",
      },
      scope: "write",
      response: null,
    },
    {
      method: "get",
      path: "/sync/status",
      tag: "Sync",
      id: "getSyncStatus",
      summary: { en: "Delivery status", de: "Verteilstatus" },
      description: {
        en: "Connection to Microsoft 365, people by status and the last delivery run.",
        de: "Verbindung zu Microsoft 365, Personen nach Status und der letzte Verteillauf.",
      },
      scope: "read",
      response: jsonSchema(publicSyncStatusSchema, locale),
      example: {
        connection: "granted",
        running: false,
        lastSuccessAt: "2026-10-25T06:00:12.000Z",
        recipients: { pending: 0, syncing: 0, ok: 48, error: 1, unreachable: 2, removing: 0 },
        lastRun: {
          id: "c3a7e5f2-1b9d-4c6e-8a2f-7d0b4e1c9a53",
          trigger: "change",
          status: "done",
          startedAt: "2026-10-25T06:00:00.000Z",
          finishedAt: "2026-10-25T06:00:12.000Z",
          created: 3,
          updated: 51,
          deleted: 0,
          errors: 0,
        },
      },
    },
  ];
}

export type DocParameter = {
  name: string;
  in: "path" | "query";
  required: boolean;
  type: string;
  description: string;
};
export type DocOperation = Omit<Operation, "summary" | "description" | "query"> & {
  summary: string;
  description: string;
  parameters: DocParameter[];
};

/** The operations in one language, for the documentation page. */
export function apiOperations(locale: DocLocale): DocOperation[] {
  return operations(locale).map((op) => {
    const pathParams = [...op.path.matchAll(/\{(\w+)\}/g)].map(
      ([, name]): DocParameter => ({
        name: name!,
        in: "path",
        required: true,
        type: "uuid",
        description: "",
      }),
    );
    const query = op.query ? jsonSchema(op.query, locale, "input") : null;
    const queryParams = Object.entries(query?.properties ?? {}).map(
      ([name, s]): DocParameter => ({
        name,
        in: "query",
        required: false,
        type: s.format === "uuid" ? "uuid" : String(s.type ?? "string"),
        description: PARAM_DOCS[name]?.[locale] ?? "",
      }),
    );
    return {
      ...op,
      summary: op.summary[locale],
      description: op.description[locale],
      parameters: [...pathParams, ...queryParams],
    };
  });
}

export const API_BASE_PATH = "/api/v1";

/** The OpenAPI 3.1 document. */
export function buildOpenApi(serverUrl: string, locale: DocLocale = "en") {
  const paths: Record<string, Record<string, unknown>> = {};
  const errorResponse = { content: { "application/json": { schema: jsonSchema(errorSchema, locale) } } };
  for (const op of apiOperations(locale)) {
    const responses: Record<string, unknown> = {
      [op.response ? "200" : "204"]: op.response
        ? { description: "OK", content: { "application/json": { schema: op.response, example: op.example } } }
        : { description: "No content" },
      "400": { description: "Validation failed", ...errorResponse },
      "401": { description: "Missing, invalid or expired API key", ...errorResponse },
      "403": { description: "The key lacks the scope or list access", ...errorResponse },
      "404": { description: "Not found or not visible to the key", ...errorResponse },
      "409": { description: "Conflict, e.g. a read-only contact or a taken list name", ...errorResponse },
      "429": { description: "Rate limit exceeded, see Retry-After", ...errorResponse },
    };
    paths[op.path] ??= {};
    paths[op.path]![op.method] = {
      operationId: op.id,
      tags: [op.tag],
      summary: op.summary,
      description: `${op.description}\n\nScope: ${op.scope}${op.allLists ? " (key not limited to lists)" : ""}.`,
      parameters: op.parameters.map((p) => ({
        name: p.name,
        in: p.in,
        required: p.required,
        description: p.description || undefined,
        schema: p.type === "uuid" ? { type: "string", format: "uuid" } : { type: p.type },
      })),
      ...(op.body
        ? {
            requestBody: {
              required: true,
              content: { "application/json": { schema: op.body, example: op.requestExample } },
            },
          }
        : {}),
      responses,
    };
  }
  return {
    openapi: "3.1.0",
    info: {
      title: "Kontaktfix API",
      version: "1.0.0",
      description:
        "Maintain the shared contacts and lists of a Kontaktfix organization. Authenticate with an API key from Settings > API: Authorization: Bearer kf_live_...",
    },
    servers: [{ url: `${serverUrl.replace(/\/$/, "")}${API_BASE_PATH}` }],
    security: [{ apiKey: [] }],
    components: { securitySchemes: { apiKey: { type: "http", scheme: "bearer", description: "API key" } } },
    tags: [{ name: "Contacts" }, { name: "Lists" }, { name: "Sync" }],
    paths,
  };
}
